/* global Config nSdkInstance */
import { get, throttle } from 'lodash';
import { SourceType } from 'Common/models/franchise';
import PlayerManager from '../PlayerManager';
import { PlayerEvent } from '../../../player/utils';

class NielsenDCRManager extends PlayerManager {
  get enabled() {
    return !!nSdkInstance && !!this._videoMetadata.custom_attributes.nielsen_analytics_enabled;
  }

  get events() {
    return {
      [PlayerEvent.SourceLoaded]: () => {
        if (this.sourceType === SourceType.SLING_DVR) {
          return;
        }
        this.initialize();
        nSdkInstance.ggPM('loadMetadata', this.createMetadataObject());
      },
      [PlayerEvent.TimeChanged]: data => {
        this.onTimeChanged(data);
      },
      [PlayerEvent.Paused]: () => {
        nSdkInstance.ggPM('stop', this._delegate.currentTime);
      },
      [PlayerEvent.AdStarted]: () => {
        nSdkInstance.ggPM('loadMetadata', this.createAdMetadataObject());
        this.adTimeChangedHeartbeat = setInterval(() => {
          if (this._delegate._player.playerView) {
            this.lastAdPlayhead = this._delegate.currentTime;
            return nSdkInstance.ggPM('setPlayheadPosition', this._delegate.currentTime);
          }
          return nSdkInstance.ggPM('setPlayheadPosition', this.lastAdPlayhead);
        }, 1000);
      },
      [PlayerEvent.AdFinished]: () => {
        clearInterval(this.adTimeChangedHeartbeat);
        this.adTimeChangedHeartbeat = undefined;
      },
      [PlayerEvent.AdBreakFinished]: () => {
        nSdkInstance.ggPM('loadMetadata', this.createMetadataObject());
      },
      [PlayerEvent.PlaybackFinished]: () => {
        this.onTimeChanged.cancel();
        nSdkInstance.ggPM('end', this._delegate.currentTime);
      },
      [PlayerEvent.Destroy]: () => {
        // need to refactor player to use sourceunloaded here
        this.onTimeChanged.cancel();
        nSdkInstance.ggPM('stop', this.lastPlayhead);
        nSdkInstance.ggPM('end', this.lastPlayhead);
        clearInterval(this.adTimeChangedHeartbeat);
      },
      [PlayerEvent.SourceUnloaded]: () => {
        clearInterval(this.adTimeChangedHeartbeat);
      },
    };
  }

  lastAdPlayhead = 0;
  lastPlayhead = 0;

  initialize() {
    if (!this.onTimeChanged) {
      this.onTimeChanged = throttle(data => {
        this.lastPlayhead = data.time;
        nSdkInstance.ggPM('setPlayheadPosition', data.time);
      }, 1000);
    }
  }

  createAdMetadataObject() {
    const currentAd = this._delegate._player.playerView.ads.getActiveAdBreak();
    let adPlacement;

    if (currentAd.position !== 'pre' && currentAd.position !== 'post') {
      adPlacement = 'mid';
    } else {
      adPlacement = currentAd.position;
    }

    return {
      assetid: currentAd.id,
      type: `${adPlacement}roll`,
    };
  }

  createMetadataObject() {
    const assetId =
      this._delegate._player.props.type === 'live'
        ? get(this._airing, 'qvt.playback_info.asset.guid', null)
        : this._videoId;
    const franchiseName =
      this._delegate._player.props.type === 'live'
        ? get(this._airing, 'airing.name', null)
        : this._franchise.name;
    const runTime =
      this._delegate._player.props.type === 'live'
        ? (new Date(this._airing.airing.end_date_time) -
            new Date(this._airing.airing.start_date_time)) /
          60000
        : this._delegate._player.props.stream.run_time;
    const seriesId =
      this._delegate._player.props.type === 'live'
        ? get(this._airing, 'airing.content_identification.series_id', '')
        : get(this._franchise, 'content_identification.series_id', '');
    const nielsenOriginator =
      this._delegate._player.props.type === 'live'
        ? get(this._airing, 'airing.nielsen_originator_call_letters', '')
        : this._playerProps.stream.nielsen_originator_call_letters || '';

    return {
      type: 'content',
      assetid: assetId,
      program: franchiseName,
      title: franchiseName,
      length: runTime,
      mediaURL: window.location.href,
      airdate: this.getAirDate(),
      isfullepisode: 'y',
      crossId1: seriesId,
      crossId2: nielsenOriginator,
      adloadtype: this._delegate._player.props.type === 'live' ? '1' : '2',
    };
  }

  getAirDate() {
    let releaseDate;
    if (this._delegate._player.props.type === 'live') {
      const airDate = get(this._airing, 'airing.original_air_date', null);
      if (!airDate || airDate.length !== 7) return '20160101 00:00:00';
      releaseDate = new Date(
        `${airDate.substr(0, 4)}-${airDate.substr(4, 2)}-${airDate.substr(6, 2)}`,
      );
    } else {
      if (!this._franchise.releaseDate) return '20160101 00:00:00';
      releaseDate = new Date(this._franchise.releaseDate);
    }
    releaseDate.setTimezone('EST');
    return releaseDate.toString('yyyyMMdd HH:mm:ss');
  }
}

export default NielsenDCRManager;
